"use client";

import { useResolveHref } from "@/lib/links";
import { useRef } from "react";
import { gsap, PLAY_ONCE, useGSAP } from "@/lib/gsap";
import { reduceMotion } from "@/lib/motion";
import { LOGO_MASK, Logo } from "@/components/ui/Logo";
import { SocialFacebook, SocialInstagram, SocialLinkedIn, SocialYouTube } from "@/components/ui/icons";
import { CONTACT, LINKS, PRODUCTS } from "@/content/quikit";
import { productHref } from "./nav-data";

const LIVE = PRODUCTS.filter((p) => p.status === "live");
const HALF = Math.ceil(LIVE.length / 2);
const SOCIAL_ICONS = { LinkedIn: SocialLinkedIn, YouTube: SocialYouTube, Instagram: SocialInstagram, Facebook: SocialFacebook };

const COMPANY = [
  ["About us", "/about"],
  ["Blog", "/blog"],
  ["Pricing", "#pricing"],
  ["Book a demo", "#contact"],
  ["Contact", "/contact"],
];

const link = "text-[14px] text-muted transition-colors duration-200 hover:text-ink";

/** `note` adds a page-specific legal line (e.g. third-party trademark notices). */
export function Footer({ note }: { note?: string } = {}) {
  const r = useResolveHref();
  const root = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      const el = root.current;
      if (!el || reduceMotion()) return;
      gsap
        .timeline({ scrollTrigger: { trigger: el, start: "top 90%", toggleActions: PLAY_ONCE } })
        .fromTo(el, { opacity: 0, y: 70 }, { opacity: 1, y: 0, duration: 1.3, ease: "silk" })
        .fromTo(gsap.utils.toArray("[data-footer-item]", el), { opacity: 0, y: 14 }, { opacity: 1, y: 0, duration: 0.8, ease: "silk", stagger: 0.02 }, 0.25)
        .fromTo(gsap.utils.toArray("[data-logo-letter]", el), { y: LOGO_MASK }, { y: 0, duration: 1, ease: "premium", stagger: 0.05 }, 0.35)
        .fromTo(gsap.utils.toArray("[data-footer-social]", el), { opacity: 0, scale: 0.8 }, { opacity: 1, scale: 1, duration: 0.6, ease: "back.out(1.6)", stagger: 0.06 }, 0.5);
    },
    { scope: root },
  );

  return (
    <footer
      ref={root}
      data-reveal-init=""
      className="frame mb-[var(--gutter)] mt-[var(--gutter)] rounded-[var(--radius-panel)] bg-panel px-6 pb-7 pt-12 md:px-12 md:pb-9 md:pt-16"
    >
      <div className="grid gap-10 md:grid-cols-12">
        <div className="md:col-span-4">
          <p data-footer-item="" className="max-w-[280px] text-[14px] leading-relaxed text-muted">
            The AI-first Work Operating System. Connected apps, one shared AI layer.
          </p>
          <div className="mt-6 flex items-center gap-2">
            {CONTACT.socials.map(({ label, href }) => {
              const Icon = SOCIAL_ICONS[label as keyof typeof SOCIAL_ICONS];
              return (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener"
                  aria-label={`Quikit on ${label}`}
                  data-footer-social=""
                  className="grid size-9 place-items-center rounded-full bg-white text-ink shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition-[scale,background-color,color] duration-200 hover:bg-ink hover:text-white active:scale-[0.96]"
                >
                  <Icon className="size-3.5" />
                </a>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 sm:grid-cols-4 md:col-span-8">
          {[LIVE.slice(0, HALF), LIVE.slice(HALF)].map((col, ci) => (
            <div key={ci}>
              <p data-footer-item="" className="text-[14px] font-medium tracking-[-0.01em]">
                {ci === 0 ? "Products" : " "}
              </p>
              <ul className="mt-3 space-y-2">
                {col.map((p) => (
                  <li key={p.key} data-footer-item="">
                    <a href={r(productHref(p.key))} className={link}>
                      {p.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div>
            <p data-footer-item="" className="text-[14px] font-medium tracking-[-0.01em]">
              Company
            </p>
            <ul className="mt-3 space-y-2">
              {COMPANY.map(([label, href]) => (
                <li key={label} data-footer-item="">
                  <a href={r(href)} className={link}>
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p data-footer-item="" className="text-[14px] font-medium tracking-[-0.01em]">
              Contact
            </p>
            <ul className="mt-3 space-y-3 text-[14px] text-muted">
              <li data-footer-item="">
                <a href={CONTACT.phone.href} className={link}>
                  {CONTACT.phone.label}
                </a>
              </li>
              {CONTACT.offices.map((o) => (
                <li key={o.region} data-footer-item="" className="leading-[1.55]">
                  <span className="block font-medium text-ink">{o.region}</span>
                  {o.lines.map((l) => (
                    <span key={l} className="block">
                      {l}
                    </span>
                  ))}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="mt-20 flex flex-col gap-8 md:mt-24 md:flex-row md:items-end md:justify-between">
        <Logo className="w-[170px] text-ink md:w-[210px]" />
        <p data-footer-item="" className="text-[14px] text-muted">
          {CONTACT.tagline[0]} <span className="text-faint">/</span> {CONTACT.tagline[1]}
        </p>
      </div>

      <div data-footer-item="" className="mt-8 flex flex-col gap-3 border-t border-ink/[0.06] pt-6 text-[12px] text-muted md:flex-row md:items-center md:justify-between">
        <span>
          {/* The page may be prerendered in an earlier year than it is read in; a mismatched year
              must not fail hydration and re-render the whole page. */}
          © <span suppressHydrationWarning>{new Date().getFullYear()}</span> Quikit{note ? <span className="block md:inline">{`  ·  ${note}`}</span> : null}
        </span>
        <span className="flex gap-5">
          <a href={LINKS.privacy} className="hover:text-ink">
            Privacy
          </a>
          <a href={LINKS.terms} className="hover:text-ink">
            Terms
          </a>
          <a href={LINKS.security} className="hover:text-ink">
            Security
          </a>
        </span>
      </div>
    </footer>
  );
}
